import { recordValueStatusPickerOptions } from "#src/components/Common/InlineRecordValueStatusPicker/InlineRecordValueStatusPicker";
import LockConfirmationModal from "#src/components/Common/LockConfirmationModal/LockConfirmationModal";
import {
  useHasRecordLockingPermission,
  useUpdateStatusBySearch,
} from "#src/components/hooks/adapters/useRecords";
import {
  Button,
  DropdownMenu,
  DropdownMenuOption,
  Icon,
  type ButtonProps,
} from "@validereinc/common-components";
import {
  RecordInputFilterType,
  RecordValueStatusType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useState } from "react";
import styles from "./BulkRecordStatusChangeButton.module.scss";

const cx = classNames.bind(styles);

const BulkRecordStatusChangeButton = ({
  label,
  filter,
  onSuccess,
  isDisabled,
  recordsCount,
  buttonProps,
}: {
  label?: string;
  filter: RecordInputFilterType;
  recordsCount: number;
  onSuccess?: () => void;
  isDisabled?: boolean;
  buttonProps?: Partial<ButtonProps>;
}) => {
  const [newStatus, setNewStatus] = useState<RecordValueStatusType | null>(
    null
  );

  const setStatusBySearch = useUpdateStatusBySearch({
    onSuccess: () => {
      setNewStatus(null);
      onSuccess?.();
    },
  });
  const hasLockPermission = useHasRecordLockingPermission();

  const isComponentDisabled = !!isDisabled || recordsCount === 0;

  if (!hasLockPermission) return null;

  const dropdownOptions = recordValueStatusPickerOptions.map(
    ({ label, value, icon, variant }) =>
      ({
        label,
        variant,
        slotLeft: ({ iconProps }) =>
          icon ? (
            <Icon
              {...iconProps}
              variant="lock-simple"
            />
          ) : (
            <div className={cx("empty-icon-spacer")} />
          ),
        onClick: () => {
          setNewStatus(value);
        },
      }) as DropdownMenuOption
  );

  const displayButton = (
    <Button
      {...buttonProps}
      isLoading={setStatusBySearch.isLoading}
      slotLeft={<Icon variant="pencil-simple" />}
      slotRight={<Icon variant="caret-down" />}
      disabled={isComponentDisabled}
    >
      {label ?? <>Change Record Status</>}
    </Button>
  );

  if (isComponentDisabled) return displayButton;

  return (
    <>
      <DropdownMenu
        contentProps={{ align: "end" }}
        options={dropdownOptions}
      >
        {displayButton}
      </DropdownMenu>
      <LockConfirmationModal
        action={newStatus}
        onClose={() => setNewStatus(null)}
        isLoading={setStatusBySearch.isLoading}
        recordsCount={recordsCount}
        onConfirm={() => {
          setStatusBySearch.mutate({
            filter,
            status: newStatus!,
          });
        }}
      />
    </>
  );
};

export default BulkRecordStatusChangeButton;
